import { FC, useContext, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import HubInputField from "../inputType";
import { PATIENT_GENDER, PATIENT_TIME_TO_CONTACT, PATIENT_TITLE } from "src/constants/cases";
import { GroupBase, OptionsOrGroups } from "react-select";
import { APIEndpoints } from "src/types/apiTypes";
import { GlobalPatientDetailType, PatientInfoType } from "src/pages/updatePatient/types";
import useFetch from "src/hooks/useFetch";
import { useNavigate } from "react-router-dom";
import { LoaderContext } from "src/context/loaderContext";
import { UpdatePatientFormProps } from "./types";



const UpdatePatientForm: FC<UpdatePatientFormProps> = ({ patientInfo }): JSX.Element => {
  const navigate = useNavigate();
  const { hideLoader, setLoader } = useContext(LoaderContext);

  const { control, reset, handleSubmit } = useForm<PatientInfoType>({
    mode: 'all',
  });

  useEffect(() => {
    if (patientInfo)
      reset({
        firstname: patientInfo.firstname,
        middlename: patientInfo.middlename,
        lastname: patientInfo.lastname,
        dob: patientInfo.dob,
        gender: patientInfo.gender,
        ssn: patientInfo.ssn || undefined,
        best_time_to_contact: patientInfo.best_time_to_contact,
      })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientInfo]);





  const { loading: updatePatientLoading, data: updateGlobalPatientDetailObject, error: updateGlobalPatientDetailError, mutate: updateGlobalPatientById } =
    useFetch<GlobalPatientDetailType>(APIEndpoints.UPDATE_GLOBAL_PATIENT_DETAIL_BY_ID);

  useEffect(() => {
    if (updateGlobalPatientDetailObject || updateGlobalPatientDetailError) {
      hideLoader();

      if (updateGlobalPatientDetailObject)
        navigate("/cases")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateGlobalPatientDetailObject, updateGlobalPatientDetailError]);


  const updateGlobalPatient = (values:unknown) => {
    setLoader();
    const requestbody = values;
    updateGlobalPatientById(requestbody,{ patientId: patientInfo.id })
  }




  return (
    <form onSubmit={handleSubmit(updateGlobalPatient)}>
      <div className="card rounded-1">
        <div className="card-header bg-success-subtle">
          <h4 className="mb-0 text-black">Patient Info</h4>
        </div>
        <div className="card-body patient-info-labels">

          <div className="row">
            <div className="col-12 col-lg-3 col-md-4">
              <Controller
                  name="title"
                  control={control}
                  defaultValue="mr"
                  render={({field, fieldState: {error}}) => (
                      <HubInputField
                          inputId="patient_attributes.title"
                          inputLabel="Title"
                          isRequired={true}
                          isRowBased={false}
                          field={field}
                          error={error}
                          isDropdown={true}
                          isSearchable={false}
                          dropdownData={PATIENT_TITLE as unknown as OptionsOrGroups<string, GroupBase<string>>}
                      />
                  )}
              />
            </div>

            <div className="col-12 col-lg-3 col-md-4">
              <Controller
                  name="firstname"
                  control={control}
                  defaultValue=""
                  render={({field, fieldState: {error}}) => (
                      <HubInputField
                          inputId="patient_attributes.firstname"
                          inputLabel="First Name"
                          isRowBased={false}
                          isRequired={false}
                          field={field}
                          error={error}
                      />
                  )}
              />
            </div>

            <div className="col-12 col-lg-3 col-md-4">
              <Controller
                  name="middlename"
                  control={control}
                  defaultValue=""
                  render={({field, fieldState: {error}}) => (
                      <HubInputField
                          isRowBased={false}
                          isRequired={false}
                          field={field}
                          inputId="patient_attributes.middlename"
                          inputLabel="Middle Name"
                          error={error}
                      />
                  )}
              />
            </div>

            <div className="col-12 col-lg-3 col-md-4">
              <Controller
                  name="lastname"
                  control={control}
                  defaultValue=""
                  render={({field, fieldState: {error}}) => (
                      <HubInputField
                          isRowBased={false}
                          isRequired={false}
                          field={field}
                          inputId="patient_attributes.lastname"
                          inputLabel="Last Name"
                          error={error}
                      />
                  )}
              />
            </div>

            <div className="col-12 col-lg-3 col-md-4">
              <Controller
                  name="gender"
                  control={control}
                  defaultValue="M"
                  render={({field, fieldState: {error}}) => (
                      <HubInputField
                          inputId="patient_attributes.gender"
                          inputLabel="Gender"
                          isRequired={false}
                          isRowBased={false}
                          field={field}
                          error={error}
                          isDropdown={true}
                          isSearchable={false}
                          dropdownData={PATIENT_GENDER as unknown as OptionsOrGroups<string, GroupBase<string>>}
                      />
                  )}
              />
            </div>

            <div className="col-12 col-lg-3 col-md-4">
              <Controller
                  name="dob"
                  control={control}
                  defaultValue=""
                  render={({field, fieldState: {error}}) => (
                      <HubInputField
                          inputId="patient_attributes.dob"
                          inputLabel="Date of birth"
                          inputType="date"
                          isRowBased={false}
                          isRequired={false}
                          error={error}
                          field={field}
                      />
                  )}
              />
            </div>

            <div className="col-12 col-lg-3 col-md-4">
              <Controller
                  name="best_time_to_contact"
                  control={control}
                  defaultValue=""
                  render={({field, fieldState: {error}}) => (
                      <HubInputField
                          inputId="patient_attributes.best_time_to_contact"
                          inputLabel="Contact Time"
                          inputPlaceholder="Select Time..."
                          isRequired={false}
                          isRowBased={false}
                          field={field}
                          error={error}
                          isDropdown={true}
                          isSearchable={false}
                          dropdownData={
                            (
                              [ { value: "", label: "Select Time..." }, ...PATIENT_TIME_TO_CONTACT]
                            ) as unknown as OptionsOrGroups<string, GroupBase<string>>
                          }
                      />
                  )}
              />
            </div>

            <div className="col-12 col-lg-3 col-md-4">
              <Controller
                  name="ssn"
                  control={control}
                  defaultValue=""
                  render={({field, fieldState: {error}}) => (
                      <HubInputField
                          isRowBased={false}
                          isRequired={false}
                          field={field}
                          inputId="patient_attributes.ssn"
                          inputLabel="SSN"
                          error={error}
                      />
                  )}
              />
            </div>
          </div>

          <div className="row justify-content-end">
            <div className="col-12 text-end">
              <button type="submit" className="btn btn-blue">
                {updatePatientLoading ? (<i className="spinner-border me-2"></i>) : null}
                Update Global Patient
              </button>
            </div>
          </div>

        </div>
      </div>
    </form>
  )
}

export default UpdatePatientForm;
